import React, { useState, useEffect } from 'react';
import {
  Container, Card, Row, Col, Button, Accordion,
  Modal, Form, Badge, Spinner, Alert
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faTrash, faPencilAlt, faCheckCircle, faBrain, faInfoCircle,
  faQuestionCircle, faStar, faChartBar, faExternalLinkAlt, faLightbulb
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useUser } from '../../contexts/UserContext';
import CHAT_API_URL from '../../utils/chatApiUrl';

const API_BASE_URL = CHAT_API_URL;
const API_KEY = process.env.REACT_APP_CHAT_API_KEY || 'test-api-key-123';

const MAX_CUSTOM_CATEGORIES = 5;
const MISSION_STATEMENT_KEY = 'mission_statement';

const CATEGORY_FIELDS = [
  { key: 'foundational_description', label: 'Foundational Description', placeholder: 'Describe the core purpose and mission of this category…' },
  { key: 'current_reality', label: 'Current Reality', placeholder: 'What is the current state of this area in your organization…' },
  { key: 'tensions_pain_points', label: 'Tensions & Pain Points', placeholder: 'What challenges, conflicts, or friction exist here…' },
  { key: 'guardrails', label: 'Guardrails', placeholder: 'What boundaries, constraints, or rules apply to this area…' },
];

const API_HEADERS = { 'Content-Type': 'application/json', 'X-API-Key': API_KEY };

// The Harmony ecc/OCP API namespaces organizations across products (leanfitt / leadershipfitt),
// so it identifies an org by `leanfitt:org:<uuid>`, NOT the bare LeanFITT uuid. resolve_viewer +
// membership.find compare this qualified id, so a bare uuid is refused (403). This app's orgs are
// always LeanFITT. ponytail: hard-coded prefix; if LeadershipFITT orgs ever reach this screen,
// resolve the qualified id from GET /api/ecc/organizations instead.
const eccOrgId = (id) => `leanfitt:org:${id}`;

const completionPercent = (cat, drafts) => {
  const draft = drafts[cat.category_key] || {};
  const filled = CATEGORY_FIELDS.filter(f => (draft[f.key] || '').trim().length > 0).length;
  return Math.round((filled / CATEGORY_FIELDS.length) * 100);
};

// A category counts as "established" only when all four fields are saved (non-empty).
// Keyed off saved data (not the live draft) so the rest of the OCP unlocks only after a save.
const isCategoryComplete = (cat) =>
  !!cat && CATEGORY_FIELDS.every(f => (cat[f.key] || '').trim().length > 0);

function OCPSetup() {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const { user, appSub } = useUser();

  const isOwner = activeOrganization?.owner === user?.attributes?.sub;
  const isCoOwner = activeOrganization?.additionalOwners?.includes(user?.attributes?.email);
  const isOrgAdmin = isOwner || isCoOwner;

  // The ecc guard requires the VIEWER's identity to resolve which orgs they can see; without it the
  // API returns 400 "a viewer sub or email is required". Use appSub (User.cognitoID → survives the
  // FITTWorks hub migration), with email as the LeadershipFITT-side fallback.
  const authHeaders = {
    ...API_HEADERS,
    'X-Actor-Id': appSub || user?.attributes?.sub || '',
    'X-Actor-Email': user?.attributes?.email || '',
  };

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [outsideFactors, setOutsideFactors] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [activeTab, setActiveTab] = useState('categories');
  const [error, setError] = useState(null);

  // Per-category draft state
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  // Add custom category modal
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryKey, setNewCategoryKey] = useState('');

  // Delete category confirm
  const [deleteCategory, setDeleteCategory] = useState(null);

  // Outside factors state
  const [showAddFactor, setShowAddFactor] = useState(false);
  const [editFactor, setEditFactor] = useState(null);
  const [factorForm, setFactorForm] = useState({ title: '', description: '' });
  const [deleteFactor, setDeleteFactor] = useState(null);

  // OCP directive modal (first visit)
  const [showDirectiveModal, setShowDirectiveModal] = useState(false);
  const [neverShowAgain, setNeverShowAgain] = useState(false);

  // OCP info/explainer modal
  const [showOcpInfo, setShowOcpInfo] = useState(false);

  // Mission Statement "why this matters?" micro-modal
  const [showMissionInfo, setShowMissionInfo] = useState(false);

  useEffect(() => {
    if (!isOrgAdmin) {
      navigate('/dashboard', { replace: true });
      return;
    }
    loadProfile();
    checkModalState();
  }, [activeOrganization?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProfile = async (skipAutoInit = false) => {
    if (!activeOrganization?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [profileRes, factorsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/admin/organizations/${encodeURIComponent(eccOrgId(activeOrganization.id))}/ocp`, { headers: authHeaders }),
        axios.get(`${API_BASE_URL}/admin/organizations/${encodeURIComponent(eccOrgId(activeOrganization.id))}/ocp/outside-factors`, { headers: authHeaders }),
      ]);

      const cats = profileRes.data?.categories || [];

      if (cats.length === 0 && !skipAutoInit) {
        setLoading(false);
        await axios.post(`${API_BASE_URL}/admin/organizations/${encodeURIComponent(eccOrgId(activeOrganization.id))}/ocp/initialize`, {}, { headers: authHeaders });
        await loadProfile(true);
        return;
      }

      setCategories(cats);
      setIsInitialized(cats.length > 0);
      const draftMap = {};
      for (const cat of cats) {
        draftMap[cat.category_key] = {
          foundational_description: cat.foundational_description || '',
          current_reality: cat.current_reality || '',
          tensions_pain_points: cat.tensions_pain_points || '',
          guardrails: cat.guardrails || '',
        };
      }
      setDrafts(draftMap);

      const factorsData = factorsRes.data;
      setOutsideFactors(Array.isArray(factorsData) ? factorsData : (factorsData?.factors || []));
    } catch (e) {
      console.error('Error loading OCP profile:', e);
      setError('Failed to load OCP data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const checkModalState = async () => {
    if (!activeOrganization?.id || !user?.attributes?.sub) return;
    try {
      const userId = user.attributes.sub;
      const res = await axios.get(
        `${API_BASE_URL}/admin/organizations/${encodeURIComponent(eccOrgId(activeOrganization.id))}/ocp/modal?user_id=${encodeURIComponent(userId)}`,
        { headers: authHeaders }
      );
      if (res.data?.should_show) setShowDirectiveModal(true);
    } catch (e) {
      // silently ignore
    }
  };

  const handleDraftChange = (categoryKey, field, value) => {
    setDrafts(prev => ({
      ...prev,
      [categoryKey]: { ...prev[categoryKey], [field]: value },
    }));
  };

  const handleSaveCategory = async (cat) => {
    if (!missionComplete && cat.category_key !== MISSION_STATEMENT_KEY) {
      setError('Complete the Mission Statement before editing other categories.');
      return;
    }
    setSavingKey(cat.category_key);
    const draft = drafts[cat.category_key] || {};
    try {
      await axios.put(
        `${API_BASE_URL}/admin/organizations/${encodeURIComponent(eccOrgId(activeOrganization.id))}/ocp/categories/${encodeURIComponent(cat.category_key)}`,
        draft,
        { headers: authHeaders }
      );
      setCategories(prev =>
        prev.map(c => c.category_key === cat.category_key ? { ...c, ...draft } : c)
      );
    } catch (e) {
      console.error('Error saving category:', e);
      setError(`Failed to save "${cat.display_name}". Please try again.`);
    } finally {
      setSavingKey(null);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    if (!missionComplete) {
      setError('Complete the Mission Statement before adding categories.');
      return;
    }
    setActionLoading(true);
    const key = newCategoryKey.trim() || newCategoryName.trim().toLowerCase().replace(/\s+/g, '_');
    try {
      await axios.post(
        `${API_BASE_URL}/admin/organizations/${encodeURIComponent(eccOrgId(activeOrganization.id))}/ocp/categories`,
        { category_key: key, display_name: newCategoryName.trim() },
        { headers: authHeaders }
      );
      setShowAddCategory(false);
      setNewCategoryName('');
      setNewCategoryKey('');
      await loadProfile();
    } catch (e) {
      console.error('Error creating category:', e);
      setError('Failed to create category. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteCategory) return;
    setActionLoading(true);
    try {
      await axios.delete(
        `${API_BASE_URL}/admin/organizations/${encodeURIComponent(eccOrgId(activeOrganization.id))}/ocp/categories/${encodeURIComponent(deleteCategory.category_key)}`,
        { headers: authHeaders }
      );
      setDeleteCategory(null);
      await loadProfile();
    } catch (e) {
      console.error('Error deleting category:', e);
      setError('Failed to remove category. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveFactor = async () => {
    if (!factorForm.title.trim()) return;
    if (!missionComplete) {
      setError('Complete the Mission Statement before adding outside factors.');
      return;
    }
    setActionLoading(true);
    try {
      if (editFactor) {
        await axios.put(
          `${API_BASE_URL}/admin/organizations/${encodeURIComponent(eccOrgId(activeOrganization.id))}/ocp/outside-factors/${editFactor.id}`,
          factorForm,
          { headers: authHeaders }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/admin/organizations/${encodeURIComponent(eccOrgId(activeOrganization.id))}/ocp/outside-factors`,
          factorForm,
          { headers: authHeaders }
        );
      }
      setShowAddFactor(false);
      setEditFactor(null);
      setFactorForm({ title: '', description: '' });
      const res = await axios.get(
        `${API_BASE_URL}/admin/organizations/${encodeURIComponent(eccOrgId(activeOrganization.id))}/ocp/outside-factors`,
        { headers: authHeaders }
      );
      const factorsData = res.data;
      setOutsideFactors(Array.isArray(factorsData) ? factorsData : (factorsData?.factors || []));
    } catch (e) {
      console.error('Error saving factor:', e);
      setError('Failed to save factor. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteFactor = async () => {
    if (!deleteFactor) return;
    setActionLoading(true);
    try {
      await axios.delete(
        `${API_BASE_URL}/admin/organizations/${encodeURIComponent(eccOrgId(activeOrganization.id))}/ocp/outside-factors/${deleteFactor.id}`,
        { headers: authHeaders }
      );
      setDeleteFactor(null);
      const res = await axios.get(
        `${API_BASE_URL}/admin/organizations/${encodeURIComponent(eccOrgId(activeOrganization.id))}/ocp/outside-factors`,
        { headers: authHeaders }
      );
      const factorsData = res.data;
      setOutsideFactors(Array.isArray(factorsData) ? factorsData : (factorsData?.factors || []));
    } catch (e) {
      console.error('Error deleting factor:', e);
      setError('Failed to remove factor. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDismissDirective = async () => {
    setShowDirectiveModal(false);
    try {
      const userId = user?.attributes?.sub;
      if (userId) {
        await axios.post(
          `${API_BASE_URL}/admin/organizations/${encodeURIComponent(eccOrgId(activeOrganization.id))}/ocp/modal/dismiss?user_id=${encodeURIComponent(userId)}&never_show_again=${neverShowAgain}`,
          {},
          { headers: authHeaders }
        );
      }
    } catch (e) {
      // silently ignore
    }
  };

  const mandatoryCategories = categories.filter(c => c.category_type === 'mandatory');
  const customCategories = categories.filter(c => c.category_type === 'custom');
  const canAddCustom = customCategories.length < MAX_CUSTOM_CATEGORIES;
  const missionComplete = isCategoryComplete(categories.find(c => c.category_key === MISSION_STATEMENT_KEY));

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="success" />
        <p className="mt-3 text-muted">Loading OCP data…</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center">
          <FontAwesomeIcon icon={faBrain} className="text-success me-3" size="2x" />
          <div>
            <h2 className="mb-0 fw-bold">Harmony™ OCP</h2>
            <p className="text-muted mb-0 small">Organizational Consciousness Platform — Give your AI Mentor deep organizational context</p>
          </div>
        </div>
        <Button
          variant="outline-info"
          size="sm"
          onClick={() => setShowOcpInfo(true)}
          title="What is OCP?"
        >
          <FontAwesomeIcon icon={faQuestionCircle} className="me-1" />
          What is OCP?
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!missionComplete && (
        <Alert variant="warning" className="d-flex align-items-start">
          <FontAwesomeIcon icon={faInfoCircle} className="me-2 mt-1" />
          <div>
            <p className="mb-2">
              Before administrators configure key business drivers, operational priorities, safety concerns,
              and organizational parameters, a <strong>Mission Statement</strong> must first be established.
            </p>
            <p className="mb-2">
              This Mission Statement serves as the foundational guidance layer for the FITTWorks™ Actionable
              Intelligence System.
            </p>
            <p className="mb-2">
              By defining the organization's mission and strategic intent upfront, Harmony AI can align
              responses, recommendations, workflows, and improve intelligence with the organization's
              operational goals, leadership expectations, values, and decision-making priorities.
            </p>
            <p className="mb-1">This ensures that:</p>
            <ul className="mb-2">
              <li>AI-generated guidance remains organizationally aligned</li>
              <li>operational recommendations support strategic objectives</li>
              <li>leadership and process decisions follow a unified direction</li>
              <li>safety, culture, and business priorities remain embedded across system interactions</li>
            </ul>
            <p className="mb-0">
              The Mission Statement becomes the contextual foundation through which organizational intelligence
              is interpreted across LeanFITT™, LeadershipFITT™, and Harmony AI.
            </p>
          </div>
        </Alert>
      )}

      {/* Tab Navigation */}
      <div className="d-flex gap-2 mb-4">
        <Button
          variant={activeTab === 'categories' ? 'success' : 'outline-secondary'}
          size="sm"
          onClick={() => setActiveTab('categories')}
        >
          Categories
        </Button>
        <Button
          variant={activeTab === 'outside-factors' ? 'success' : 'outline-secondary'}
          size="sm"
          onClick={() => setActiveTab('outside-factors')}
        >
          Outside Factors
        </Button>
      </div>

      {/* ── Categories Tab ─────────────────────────────────── */}
      {activeTab === 'categories' && (
        <div>
          {/* See How Harmony AI Thinks — worked example (top, after the mission alert) */}
          <div className="mb-4">
            <Accordion>
              <Accordion.Item eventKey="harmony-demo" className="border-0 shadow-sm rounded overflow-hidden">
                <Accordion.Header>
                  <span className="fw-bold text-success">
                    <FontAwesomeIcon icon={faLightbulb} className="me-2" />
                    See How Harmony AI Evaluates Mission Conflicts
                  </span>
                </Accordion.Header>
                <Accordion.Body className="small" style={{ backgroundColor: '#fafbfc' }}>
                  {/* Mission */}
                  <div className="p-3 rounded mb-3" style={{ backgroundColor: '#ecfdf5', borderLeft: '4px solid #10b981' }}>
                    <div className="fw-bold text-success mb-1">
                      <FontAwesomeIcon icon={faStar} className="me-1" />Mission Statement
                    </div>
                    <div className="fst-italic">
                      Deliver reliable, high-quality customer service through disciplined execution, accountable
                      leadership, and sustainable team performance.
                    </div>
                  </div>

                  {/* The Request */}
                  <div className="p-3 rounded mb-3" style={{ backgroundColor: '#fef2f2', borderLeft: '4px solid #ef4444' }}>
                    <div className="fw-bold text-danger mb-2">
                      The Request <Badge bg="danger" className="ms-1 fw-normal">counter to mission</Badge>
                    </div>
                    <div className="fst-italic mb-2">
                      “We’re behind on response times this week. Should I have the team skip documentation and just
                      close tickets faster?”
                    </div>
                    <div className="mb-1">It may improve short-term speed, but it:</div>
                    <ul className="mb-1">
                      <li>undermines quality</li>
                      <li>reduces accountability</li>
                      <li>creates process inconsistency</li>
                      <li>increases long-term system risk</li>
                    </ul>
                    <div className="text-muted">That is exactly the kind of tension OCP is meant to govern.</div>
                  </div>

                  {/* Assessment */}
                  <div className="p-3 rounded mb-3" style={{ backgroundColor: '#eff6ff', borderLeft: '4px solid #3b82f6' }}>
                    <div className="fw-bold text-primary mb-1">
                      <FontAwesomeIcon icon={faBrain} className="me-1" />Harmony AI Assessment
                    </div>
                    <p className="mb-2">
                      This action may improve short-term response speed, but it conflicts with the current mission
                      boundary. Your mission prioritizes reliable, high-quality customer service through disciplined
                      execution, accountable leadership, and sustainable team performance. Skipping documentation may
                      reduce immediate cycle time, but it introduces risk across quality, accountability, and long-term
                      execution reliability.
                    </p>
                    <div className="fw-semibold">Recommended response: address throughput without removing process controls.</div>
                  </div>

                  {/* Four Lens */}
                  <div className="fw-bold text-uppercase text-muted mb-2" style={{ letterSpacing: '0.05em', fontSize: '0.75rem' }}>
                    Four Lens Response
                  </div>

                  <div className="border rounded p-3 mb-2 bg-white">
                    <Badge bg="primary" className="mb-2">People &amp; Leadership · LeadershipFITT™</Badge>
                    <p className="mb-2">
                      Skipping documentation signals to the team that speed is valued over discipline. This creates
                      leadership inconsistency and weakens accountability standards. Over time, teams begin optimizing
                      for urgency rather than quality, which increases error tolerance and reduces execution trust.
                    </p>
                    <div className="px-2 py-1 rounded d-inline-block" style={{ backgroundColor: '#fef2f2', color: '#b91c1c' }}>
                      Leadership risk: short-term speed gain, long-term discipline erosion.
                    </div>
                  </div>

                  <div className="border rounded p-3 mb-2 bg-white">
                    <Badge bg="success" className="mb-2">Process Optimization · LeanFITT™</Badge>
                    <p className="mb-2">
                      Documentation is not administrative overhead in this context — it is part of process control.
                      Removing it may improve short-term throughput, but it also:
                    </p>
                    <ul className="mb-2">
                      <li>reduces traceability</li>
                      <li>weakens root cause visibility</li>
                      <li>increases repeat failure risk</li>
                      <li>degrades process reliability</li>
                    </ul>
                    <div className="px-2 py-1 rounded d-inline-block mb-2" style={{ backgroundColor: '#fef2f2', color: '#b91c1c' }}>
                      Process risk: faster ticket closure, lower system quality.
                    </div>
                    <div><strong>Recommended Lean action:</strong> identify where documentation is creating bottlenecks and simplify the step rather than remove it.</div>
                  </div>

                  <div className="border rounded p-3 mb-2 bg-white">
                    <Badge bg="warning" text="dark" className="mb-2">Work-Life Balance</Badge>
                    <p className="mb-2">
                      Skipping process controls under pressure usually shifts strain downstream. The team may close
                      tickets faster today, but unresolved errors, repeat issues, and rework often increase tomorrow’s
                      workload. This creates a short-term relief / long-term overload pattern.
                    </p>
                    <div className="px-2 py-1 rounded d-inline-block" style={{ backgroundColor: '#fffbeb', color: '#b45309' }}>
                      Workload risk: lower immediate pressure, higher sustained fatigue.
                    </div>
                  </div>

                  <div className="border rounded p-3 mb-3 bg-white">
                    <Badge className="mb-2" style={{ backgroundColor: '#7c3aed' }}>Values &amp; Alignment</Badge>
                    <p className="mb-2">
                      The request conflicts with the mission’s stated operating values: disciplined execution,
                      accountable leadership, and sustainable performance. The issue is not speed. The issue is whether
                      the method used to increase speed remains aligned with how the organization has defined effective
                      performance.
                    </p>
                    <div className="px-2 py-1 rounded d-inline-block" style={{ backgroundColor: '#fef2f2', color: '#b91c1c' }}>
                      Alignment risk: local optimization at the expense of mission integrity.
                    </div>
                  </div>

                  {/* Recommendation */}
                  <div className="p-3 rounded mb-3" style={{ backgroundColor: '#ecfdf5', borderLeft: '4px solid #10b981' }}>
                    <div className="fw-bold text-success mb-1">
                      <FontAwesomeIcon icon={faCheckCircle} className="me-1" />Harmony AI Recommendation
                    </div>
                    <div className="mb-1">Do not remove documentation entirely. Instead:</div>
                    <ul className="mb-1">
                      <li>Reduce documentation to minimum required fields</li>
                      <li>Identify where the actual bottleneck exists</li>
                      <li>Streamline the documentation step</li>
                      <li>Preserve traceability and accountability</li>
                      <li>Monitor whether cycle-time gains hold without increasing rework</li>
                    </ul>
                    <div className="text-muted">This preserves speed improvement without violating mission constraints.</div>
                  </div>

                  {/* Why this matters */}
                  <div className="p-3 rounded mb-3 border bg-white">
                    <div className="fw-bold mb-2">Why this matters in OCP</div>
                    <p className="mb-2">
                      This is the real purpose of requiring a Mission Statement in OCP: it is not branding. It is the
                      governing constraint layer that prevents local decisions from optimizing against the wrong objective.
                    </p>
                    <div className="mb-1">
                      Without OCP, the system might recommend:
                      <span className="fst-italic" style={{ color: '#b91c1c' }}> “Yes, skip documentation to improve response speed.”</span>
                    </div>
                    <div className="mb-2">
                      With OCP, Harmony AI recognizes:
                      <span className="fst-italic text-success"> “That improves one metric while violating the mission-defined operating model.”</span>
                    </div>
                    <div>
                      That is the difference between <span className="fw-semibold" style={{ color: '#b91c1c' }}>reactive optimization</span> and
                      <span className="fw-semibold text-success"> governed active intelligence</span>.
                    </div>
                  </div>

                  {/* Activity Intelligence */}
                  <div className="p-3 rounded text-white" style={{ background: 'linear-gradient(135deg, #10b981, #3b82f6)' }}>
                    <div className="fw-bold mb-1">
                      <FontAwesomeIcon icon={faBrain} className="me-1" />This is Activity Intelligence!
                    </div>
                    <div>
                      The more organizational activity the system observes, the more context-aware, prescriptive, and
                      operationally aligned its intelligence becomes.
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </div>

          {/* Core Categories */}
          <div className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="text-uppercase text-muted fw-semibold mb-0" style={{ fontSize: '0.8rem', letterSpacing: '0.05em' }}>
                Core Categories ({mandatoryCategories.length}/5)
              </h5>
            </div>
            <Accordion alwaysOpen>
              {mandatoryCategories.map((cat, idx) => {
                const pct = completionPercent(cat, drafts);
                const isSaving = savingKey === cat.category_key;
                const locked = !missionComplete && cat.category_key !== MISSION_STATEMENT_KEY;
                const isMission = cat.category_key === MISSION_STATEMENT_KEY;
                return (
                  <Accordion.Item eventKey={String(idx)} key={cat.category_key} className="mb-2 border rounded">
                    <Accordion.Header>
                      <div className="d-flex align-items-center gap-2 w-100">
                        <span className="fw-semibold">{cat.display_name}</span>
                        <Badge bg="primary" className="ms-1">Core</Badge>
                        {locked && <Badge bg="secondary" className="ms-1">🔒 Locked</Badge>}
                        {pct === 100 && (
                          <Badge bg="success" className="ms-1">
                            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />Complete
                          </Badge>
                        )}
                        <span className="text-muted small ms-auto me-3">{pct}% complete</span>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      {locked && (
                        <div className="small text-muted mb-3">
                          <FontAwesomeIcon icon={faInfoCircle} className="me-1 text-warning" />
                          Complete and save the <strong>Mission Statement</strong> to edit this section.
                        </div>
                      )}
                      {isMission && (
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                          <span className="small text-muted fst-italic">
                            This becomes Harmony AI's governing decision boundary.
                          </span>
                          <Button
                            variant="link"
                            size="sm"
                            className="text-decoration-none p-0"
                            onClick={() => setShowMissionInfo(true)}
                          >
                            <FontAwesomeIcon icon={faInfoCircle} className="me-1" />Why this matters?
                          </Button>
                        </div>
                      )}
                      <Row className="g-3">
                        {CATEGORY_FIELDS.map(field => (
                          <Col md={6} key={field.key}>
                            <Form.Group>
                              <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.75rem' }}>
                                {field.label}
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder={field.placeholder}
                                value={drafts[cat.category_key]?.[field.key] || ''}
                                onChange={e => handleDraftChange(cat.category_key, field.key, e.target.value)}
                                disabled={locked}
                              />
                            </Form.Group>
                          </Col>
                        ))}
                      </Row>
                      <div className="d-flex justify-content-end mt-3">
                        <Button
                          variant="success"
                          size="sm"
                          disabled={isSaving || locked}
                          onClick={() => handleSaveCategory(cat)}
                        >
                          {isSaving ? <><Spinner animation="border" size="sm" className="me-1" />Saving…</> : 'Save'}
                        </Button>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </div>

          {/* Custom Categories */}
          <div>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="text-uppercase text-muted fw-semibold mb-0" style={{ fontSize: '0.8rem', letterSpacing: '0.05em' }}>
                Custom Categories ({customCategories.length}/{MAX_CUSTOM_CATEGORIES})
              </h5>
              <Button
                variant="outline-success"
                size="sm"
                disabled={!canAddCustom || !missionComplete}
                onClick={() => setShowAddCategory(true)}
              >
                <FontAwesomeIcon icon={faPlus} className="me-1" />
                Add Category
                {!canAddCustom && <span className="ms-1 text-muted">(limit reached)</span>}
                {canAddCustom && !missionComplete && <span className="ms-1 text-muted">(Mission Statement required)</span>}
              </Button>
            </div>

            {customCategories.length === 0 ? (
              <div
                className="text-center py-5 rounded text-muted border border-2"
                style={{ borderStyle: 'dashed', fontSize: '0.9rem' }}
              >
                No custom categories yet. Add up to {MAX_CUSTOM_CATEGORIES} custom categories
                to tailor the OCP to your organization's unique context.
              </div>
            ) : (
              <Accordion alwaysOpen>
                {customCategories.map((cat, idx) => {
                  const pct = completionPercent(cat, drafts);
                  const isSaving = savingKey === cat.category_key;
                  const locked = !missionComplete;
                  return (
                    <Accordion.Item eventKey={String(idx)} key={cat.category_key} className="mb-2 border rounded">
                      <Accordion.Header>
                        <div className="d-flex align-items-center gap-2 w-100">
                          <span className="fw-semibold">{cat.display_name}</span>
                          <Badge bg="secondary" className="ms-1">Custom</Badge>
                          {locked && <Badge bg="dark" className="ms-1">🔒 Locked</Badge>}
                          {pct === 100 && (
                            <Badge bg="success" className="ms-1">
                              <FontAwesomeIcon icon={faCheckCircle} className="me-1" />Complete
                            </Badge>
                          )}
                          <span className="text-muted small ms-auto me-3">{pct}% complete</span>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        {locked && (
                          <div className="small text-muted mb-3">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-1 text-warning" />
                            Complete and save the <strong>Mission Statement</strong> to edit this section.
                          </div>
                        )}
                        <Row className="g-3">
                          {CATEGORY_FIELDS.map(field => (
                            <Col md={6} key={field.key}>
                              <Form.Group>
                                <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.75rem' }}>
                                  {field.label}
                                </Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={3}
                                  placeholder={field.placeholder}
                                  value={drafts[cat.category_key]?.[field.key] || ''}
                                  onChange={e => handleDraftChange(cat.category_key, field.key, e.target.value)}
                                  disabled={locked}
                                />
                              </Form.Group>
                            </Col>
                          ))}
                        </Row>
                        <div className="d-flex justify-content-between align-items-center mt-3">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => setDeleteCategory(cat)}
                          >
                            <FontAwesomeIcon icon={faTrash} className="me-1" />Remove
                          </Button>
                          <Button
                            variant="success"
                            size="sm"
                            disabled={isSaving || locked}
                            onClick={() => handleSaveCategory(cat)}
                          >
                            {isSaving ? <><Spinner animation="border" size="sm" className="me-1" />Saving…</> : 'Save'}
                          </Button>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            )}
          </div>
        </div>
      )}

      {/* ── Outside Factors Tab ────────────────────────────── */}
      {activeTab === 'outside-factors' && (
        <div>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <p className="text-muted small mb-0">
              External forces that influence your organization's strategy and decision-making.
            </p>
            <Button
              variant="outline-success"
              size="sm"
              disabled={!missionComplete}
              onClick={() => { setEditFactor(null); setFactorForm({ title: '', description: '' }); setShowAddFactor(true); }}
            >
              <FontAwesomeIcon icon={faPlus} className="me-1" />Add Factor
              {!missionComplete && <span className="ms-1 text-muted">(Mission Statement required)</span>}
            </Button>
          </div>

          {outsideFactors.length === 0 ? (
            <div
              className="text-center py-5 rounded text-muted border border-2"
              style={{ borderStyle: 'dashed', fontSize: '0.9rem' }}
            >
              No outside factors added yet. Outside factors help the AI understand
              external influences on your organization (e.g. market competition, regulatory changes).
            </div>
          ) : (
            <Accordion alwaysOpen>
              {outsideFactors.map((factor, idx) => (
                <Accordion.Item eventKey={String(idx)} key={factor.id} className="mb-2 border rounded">
                  <Accordion.Header>
                    <span className="fw-semibold">
                      {factor.title || factor.description?.substring(0, 60)}
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    {factor.description && (
                      <p className="text-muted small mb-3">{factor.description}</p>
                    )}
                    <div className="d-flex justify-content-between">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => setDeleteFactor(factor)}
                      >
                        <FontAwesomeIcon icon={faTrash} className="me-1" />Remove
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          setEditFactor(factor);
                          setFactorForm({ title: factor.title || '', description: factor.description || '' });
                          setShowAddFactor(true);
                        }}
                      >
                        <FontAwesomeIcon icon={faPencilAlt} className="me-1" />Edit
                      </Button>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          )}
        </div>
      )}

      {/* ── Add/Edit Outside Factor Modal ──────────────────── */}
      <Modal show={showAddFactor} onHide={() => { setShowAddFactor(false); setEditFactor(null); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editFactor ? 'Edit Factor' : 'Add Outside Factor'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Factor Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. Market competition, Regulatory changes…"
              value={factorForm.title}
              onChange={e => setFactorForm(f => ({ ...f, title: e.target.value }))}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="fw-semibold">Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Describe how this factor affects the organization…"
              value={factorForm.description}
              onChange={e => setFactorForm(f => ({ ...f, description: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowAddFactor(false); setEditFactor(null); }}>
            Cancel
          </Button>
          <Button variant="success" disabled={actionLoading} onClick={handleSaveFactor}>
            {actionLoading ? <Spinner animation="border" size="sm" className="me-1" /> : null}
            {editFactor ? 'Update' : 'Add'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Add Custom Category Modal ───────────────────────── */}
      <Modal show={showAddCategory} onHide={() => setShowAddCategory(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Custom Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Category Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. Customer Success, Innovation Culture…"
              value={newCategoryName}
              onChange={e => setNewCategoryName(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="fw-semibold">Category Key <span className="text-muted fw-normal">(optional)</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="Auto-generated if left blank"
              value={newCategoryKey}
              onChange={e => setNewCategoryKey(e.target.value)}
            />
            <Form.Text className="text-muted">A unique identifier, e.g. customer_success</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddCategory(false)}>Cancel</Button>
          <Button variant="success" disabled={actionLoading} onClick={handleAddCategory}>
            {actionLoading ? <Spinner animation="border" size="sm" className="me-1" /> : null}
            Create
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Delete Category Confirm ─────────────────────────── */}
      <Modal show={!!deleteCategory} onHide={() => setDeleteCategory(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remove Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            Are you sure you want to remove <strong>{deleteCategory?.display_name}</strong>?
            This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteCategory(null)}>Cancel</Button>
          <Button variant="danger" disabled={actionLoading} onClick={handleDeleteCategory}>
            {actionLoading ? <Spinner animation="border" size="sm" className="me-1" /> : null}
            Remove
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Delete Factor Confirm ───────────────────────────── */}
      <Modal show={!!deleteFactor} onHide={() => setDeleteFactor(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remove Factor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            Are you sure you want to remove <strong>{deleteFactor?.title}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteFactor(null)}>Cancel</Button>
          <Button variant="danger" disabled={actionLoading} onClick={handleDeleteFactor}>
            {actionLoading ? <Spinner animation="border" size="sm" className="me-1" /> : null}
            Remove
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── OCP Directive Modal (first visit) ──────────────── */}
      <Modal show={showDirectiveModal} onHide={handleDismissDirective} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="text-success fw-bold">
            <FontAwesomeIcon icon={faBrain} className="me-2" />
            Why does OCP exist, and what do I need to do first?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>OCP (Organizational Consciousness Platform)</strong> sets the governing context for how
            Harmony AI interprets decisions, priorities, and performance.
          </p>
          <p>
            Your <strong>Mission Statement</strong> is the only required field because it defines the
            operational boundary Harmony AI uses to evaluate recommendations.
          </p>
          <p>
            Everything else—KBDs, values, surveys, and supporting inputs—can be added later to refine system
            intelligence over time.
          </p>
          <Form.Check
            type="checkbox"
            id="neverShowOCP"
            label="Don't show this again"
            checked={neverShowAgain}
            onChange={e => setNeverShowAgain(e.target.checked)}
            className="mt-3"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleDismissDirective}>
            I'll Do This Later
          </Button>
          <Button variant="success" onClick={() => { setActiveTab('categories'); handleDismissDirective(); }}>
            Define Mission Statement
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── OCP Info / Explainer Modal ──────────────────────── */}
      <Modal show={showOcpInfo} onHide={() => setShowOcpInfo(false)} centered size="lg">
        <Modal.Header closeButton style={{ backgroundColor: '#f0faf4', borderBottom: '2px solid #28a745' }}>
          <Modal.Title className="fw-bold">
            <FontAwesomeIcon icon={faBrain} className="text-success me-2" />
            Organizational Consciousness Platform (OCP)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {/* ── Explore OCP hero (intro) ─────────────────────── */}
          <div
            className="mb-4 p-4 rounded"
            style={{ background: 'linear-gradient(135deg, #e9f9ef 0%, #f7fdf9 100%)', border: '1px solid #bfe6cd' }}
          >
            <h5 className="fw-bold mb-2">Explore OCP™: Why Governance Matters in the Age of AI</h5>
            <p className="text-muted mb-2">
              Behind every intelligent recommendation is a responsibility: ensuring the right data
              leads to the right decisions.
            </p>
            <p className="text-muted mb-3">
              Discover the thinking, principles, and design philosophy behind OCP™ and how it creates
              the foundation for trustworthy, actionable organizational intelligence.
            </p>
            <Button
              variant="success"
              size="sm"
              onClick={() => document.getElementById('ocp-framework-details')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            >
              Explore the OCP™ Framework →
            </Button>
          </div>

          <p className="text-muted mb-4">
            OCP is the intelligence layer that makes your AI truly yours. It infuses every conversation
            with your organization's unique DNA — so responses aren't generic, they're aligned with
            your mission, values, and priorities.
          </p>

          <div className="mb-4" id="ocp-framework-details">
            <h6 className="fw-bold text-success d-flex align-items-center gap-2 mb-2">
              <FontAwesomeIcon icon={faQuestionCircle} />
              What Is OCP?
            </h6>
            <p className="text-muted small mb-0">
              OCP captures the core elements that define your organization: mission statement, business
              drivers, leadership principles, culture &amp; values, and employee well-being priorities.
              This structured context is fed into every AI interaction, ensuring responses are deeply
              personalized and strategically aligned.
            </p>
          </div>

          <div className="mb-4">
            <h6 className="fw-bold text-success d-flex align-items-center gap-2 mb-2">
              <FontAwesomeIcon icon={faBrain} />
              How It Works
            </h6>
            <p className="text-muted small mb-0">
              When you ask the AI a question, OCP automatically identifies which organizational categories
              are most relevant to your query. It scores the relevance of each category, selects the top
              matches, and injects that context into the AI's thinking process. The result is a response
              that understands your organization's specific challenges, goals, and guardrails — not just
              generic advice.
            </p>
          </div>

          <div className="mb-4">
            <h6 className="fw-bold text-success d-flex align-items-center gap-2 mb-2">
              <FontAwesomeIcon icon={faLightbulb} />
              The 4-Part Input Framework
            </h6>
            <p className="text-muted small mb-2">Each OCP category uses four structured sections:</p>
            <ol className="text-muted small mb-0 ps-3">
              <li><strong>Foundational Description</strong> — Your aspirational vision of what great looks like</li>
              <li><strong>Current Reality with Metrics</strong> — Where you are now with real data</li>
              <li><strong>Tensions &amp; Pain Points</strong> — The gaps and challenges you're facing</li>
              <li><strong>Non-Negotiables &amp; Guardrails</strong> — Boundaries the AI must respect in every recommendation</li>
            </ol>
          </div>

          <div className="mb-4">
            <h6 className="fw-bold text-success d-flex align-items-center gap-2 mb-2">
              <FontAwesomeIcon icon={faChartBar} />
              How Scores Are Calculated
            </h6>
            <p className="text-muted small mb-2">
              The AI evaluates your query through four lenses and assigns a score (1–10) for each:
              LeanFITT™ (process &amp; workflow), LeadershipFITT™ (people &amp; leadership), and
              Work-Life Balance (well-being). Scores are color-coded:
            </p>
            <div className="d-flex flex-wrap gap-2 mb-2">
              <Badge bg="danger" className="py-2 px-3">🔴 Red (1–4) — Urgent attention needed</Badge>
              <Badge bg="warning" text="dark" className="py-2 px-3">🟡 Yellow (5–7) — Monitor &amp; optimize</Badge>
              <Badge bg="success" className="py-2 px-3">🟢 Green (8–10) — Strong performance</Badge>
            </div>
            <p className="text-muted small mb-0">
              EverLight™ (values) provides a reflective overlay without a numeric score. Scores are 
              influenced by your OCP data, performance drivers, and the specific context of your question.
            </p>
          </div>

          <div className="mb-4">
            <h6 className="fw-bold text-success d-flex align-items-center gap-2 mb-2">
              <FontAwesomeIcon icon={faExternalLinkAlt} />
              Outside Factors
            </h6>
            <p className="text-muted small mb-0">
              Log external events — market shifts, mergers, regulatory changes, economic conditions —
              that impact your organization. The AI factors these into every response, ensuring
              recommendations account for real-world context.
            </p>
          </div>

          <div className="mb-0">
            <h6 className="fw-bold text-success d-flex align-items-center gap-2 mb-2">
              <FontAwesomeIcon icon={faStar} />
              Organizational Memory
            </h6>
            <p className="text-muted small mb-0">
              Every OCP-enriched AI interaction is stored in your organization's memory. Over time, this
              builds a living knowledge base of insights, trends, and recommendations specific to your
              organization — making the AI smarter with every conversation.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#f0faf4' }}>
          <small className="text-muted me-auto">
            💡 The richer your OCP data, the more precise and aligned your AI responses will be.
          </small>
          <Button variant="success" onClick={() => setShowOcpInfo(false)}>
            Got it
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Mission Statement "Why this matters?" Micro-Modal ── */}
      <Modal show={showMissionInfo} onHide={() => setShowMissionInfo(false)} centered size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">Why Your Mission Statement Matters?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Your Mission Statement is the governing constraint Harmony AI uses to interpret decisions.</p>
          <p>
            Before Harmony AI evaluates speed, efficiency, workload, or leadership behavior, it first checks
            whether a recommendation aligns with the mission you define here.
          </p>
          <p>
            This prevents short-term decisions from improving one metric while undermining how your organization
            defines effective performance.
          </p>
          <hr />
          <h6 className="fw-bold">Example Mission Statement</h6>
          <p className="fst-italic">
            Deliver reliable, high-quality customer service through disciplined execution, accountable leadership,
            and sustainable team performance.
          </p>
          <h6 className="fw-bold mt-3">Example Prompt to Harmony AI</h6>
          <p className="fst-italic">
            “We’re behind on response times this week. Should I have the team skip documentation and just close
            tickets faster?”
          </p>
          <h6 className="fw-bold mt-3">How Harmony AI Interprets That</h6>
          <p className="mb-1">
            Harmony AI does not evaluate that request only for speed. It evaluates whether the decision aligns with:
          </p>
          <ul>
            <li>Operational reliability</li>
            <li>Leadership accountability</li>
            <li>Sustainable execution</li>
            <li>Mission-defined performance standards (guardrails)</li>
          </ul>
          <p>
            In this case, Harmony AI would likely recommend simplifying documentation rather than removing
            it—preserving speed without violating mission constraints.
          </p>
          <hr />
          <h6 className="fw-bold">Why This Matters?</h6>
          <p className="mb-0">
            Without a mission boundary, systems optimize for speed. With OCP, Harmony AI optimizes for aligned
            performance.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={() => setShowMissionInfo(false)}>Got it</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default OCPSetup;
